import { React, useState, useApi, useParams, Dialog, DialogActions, GridRowId, CloseIcon, DataGrid, GridColDef, GridRowSelectionModel, DialogContent, DialogTitle, Divider, IconButton, Grid2, Typography, Box, useEffect } from '../../../utils/globalImports';
import { CustomButton, CustomTextField, validationMessages, Toaster } from '../../../utils/globalImports';
import { ApiResponse } from '../../../services/apiService';

interface ModalProps {
    open: boolean;  // Prop to determine if the modal is open
    onClose: () => void;  // Function to handle modal close
    intructionData: Instructed;
    feeStageData: ProposalFeeStage[];
    afterSubmit: () => void;  // Function to handle modal close
}

interface Instructed {
    intructionType: number,
    intructionValue: number,
    poAmount:number,
    instructionReference: string,
}

interface InstructedFeeStage {
    proposalID: number;
    instructionTypeID: number;
    instructionValue: number;
    instructionReference: string;
    createdBy: number;
    proposalFrameworkFeeStages: ProposalFeeStage[];
}

interface ProposalFeeStage {
    id: number;
    refrenceNumber?: string;
    description: string;
    unitPrice: number;
    unitPricePer: string;
    status?: number;
    completionNotes: string;
    proposalValue?: number;
    instructedValue: number;
    quantity?: number;
    feeStageNumber?: string;
    instructedQuantity: number;
    instructionReference?: string;
}


const ProposalAddInstructedFeeStage: React.FC<ModalProps> = ({ open, onClose, intructionData, feeStageData, afterSubmit }) => {

    const [feeStageOption, setFeeStageOption] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
    const [disabledCheckboxes, setDisabledCheckboxes] = useState<GridRowId[]>([]);
    const [nonEditableRows, setNonEditableRows] = useState<GridRowId[]>([]);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [buttonDisable, setButtonDisable] = useState<boolean>(false);

    const [errors, setErrors] = useState({
        intructionValue: '',
    });

    const [totals, setTotals] = useState<{ value: number; quantity: number }>({
        value: 0,
        quantity: 0
    });

    const { execute, loading } = useApi();

    const { proposalId } = useParams<{ proposalId?: string }>(); // Get proposalId from URL
    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL

    const userId = Number(localStorage.getItem("userId"));
    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));

    const columns: GridColDef<(typeof feeStageOption)[number]>[] = [
        {
            field: 'feeStageNumber', headerName: 'Fee Stage', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'description', headerName: 'Description', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'Price', headerName: 'Unit Price', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'quantity', headerName: 'Quantity', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'proposalValue', headerName: 'Value', flex: 1, headerAlign: 'center',
            align: 'center',
            valueFormatter: (params) => {
                const value = params as number;
                return value.toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                });
            },
        },
        // {
        //     field: 'completionNotes', headerName: 'Terms and Notes', flex: 1, headerAlign: 'center',
        //     align: 'center',
        // },
        {
            field: 'instructedQuantity', headerName: 'Instructed Quantity', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'instructedValue', headerName: 'Instructed Value', flex: 1, headerAlign: 'center',
            align: 'center',
            editable: true,
            type: 'number', // helps with built-in numeric sorting/filtering
            valueFormatter: (params: any) => {
                const value = params;

                if (value === null || value === undefined || isNaN(Number(value))) {
                    return '';
                }

                return Number(value).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                });
            },
        },
    ];

    useEffect(() => {
        setButtonDisable(false)
        setFeeStageOption(feeStageData)
        handleTotal(feeStageData)

        setErrors({
            intructionValue: '',
        });

        const preSelected: GridRowId[] = [];
        const disabled: GridRowId[] = [];
        const nonEditable: GridRowId[] = [];


        feeStageData.forEach((row) => {
            if (row.instructedValue === row.proposalValue) {
                preSelected.push(row.id);
                disabled.push(row.id);
                nonEditable.push(row.id);
            } else if (row.instructedValue > 0 && row.instructedValue < row.proposalValue!) {
                preSelected.push(row.id);
                disabled.push(row.id);
            }
        });

        setSelectedIds(preSelected);
        setDisabledCheckboxes(disabled);
        setNonEditableRows(nonEditable);
    }, [feeStageData])

    // fetch feeStages
    // const fetchProposalfeeStages = async () => {
    //     const { data } = await execute<ProposalFeeStage[]>('get', `/Proposal/feeStages/${proposalId}/${projectId}`);
    //     if (data) {
    //         const activeFeeStage = data
    //             .filter((feeStage: any) => feeStage.status)
    //             .map((feeStage) => {

    //                 const { id, refrenceNumber, feeStageNumber, instructedQuantity, description, unitPrice, unitPricePer, proposalValue, completionNotes, instructedValue, quantity } = feeStage;

    //                 return {
    //                     id,
    //                     feeStageNumber,
    //                     unitPrice,
    //                     unitPricePer,
    //                     quantity,
    //                     "Price": unitPrice + ' ' + unitPricePer,
    //                     description,
    //                     proposalValue,
    //                     completionNotes,
    //                     instructedValue,
    //                     instructedQuantity
    //                 }
    //             })

    //         handleTotal(activeFeeStage)
    //         setFeeStageOption(activeFeeStage)
    //     }
    // };

    const handleTotal = (rows: any) => {
        const totalQuantity = Number(
            rows.reduce((sum: number, row: { instructedQuantity: any; }) => {
                const val = Number(row.instructedQuantity);
                return !isNaN(val) ? sum + val : sum;
            }, 0).toFixed(2)
        );

        const totalValue = rows.reduce((sum: number, row: { instructedValue: any; }) => {
            const val = Number(row.instructedValue);
            return !isNaN(val) ? sum + val : sum;
        }, 0);


        setTotals({
            value: totalValue,         // replace with actual value
            quantity: totalQuantity    // replace with actual quantity
        });

    }

    const handleProcessRowUpdate = (updatedRow: any, oldRow: any) => {

        const errors = { intructionValue: '' };
        const val = Number(updatedRow.instructedValue);

        if (isNaN(val) || val < 0) {
            // Optional: Show error message to user
            // alert('Instructed Value must be a non-negative number.');
            return oldRow; // Revert to old row
        }

        // Validation: Cannot exceed proposalValue
        if (updatedRow.proposalValue < val) {
            errors.intructionValue = validationMessages.proposal_InstructedValueCheck("Instruction value", updatedRow.proposalValue, 0);
            setErrors(errors);
            return oldRow; // Revert to old row
        }


        const preRow = feeStageData.find(row => row.id === updatedRow.id);
        if (!preRow) return;
        // Validation: Cannot be less than original instructedValue (no reduction)
        if (preRow.instructedValue > updatedRow.instructedValue) {
            errors.intructionValue = validationMessages.proposal_InstructedValueCheck("Instruction value", updatedRow.proposalValue, preRow.instructedValue);
            setErrors(errors);
            return oldRow; // Revert to old row
        }

        setErrors(errors);

        // Calculate instructedQuantity as half of instructedValue
        const updatedRowWithQuantity = {
            ...updatedRow,
            instructedQuantity: Number((val / Number(updatedRow.unitPrice)).toFixed(2))
        };

        const updatedRows = feeStageOption.map((row) =>
            row.id === updatedRow.id ? updatedRowWithQuantity : row
        );
        setFeeStageOption(updatedRows);

        handleTotal(updatedRows)

        // Automatically select the edited row if not already selected
        setSelectedIds((prevSelected) => {
            if (!prevSelected.includes(updatedRow.id)) {
                return [...prevSelected, updatedRow.id];
            }
            return prevSelected;
        });
        return updatedRowWithQuantity;
    };

    const handleInstructedFeeStage = async () => {

        let errors = { intructionValue: '' };

        let response: ApiResponse<any>;
        const selectedRows = feeStageOption.filter((row) => selectedIds.includes(row.id));
        const hasZeroProposalValue = selectedRows.some((row) => row.instructedValue === 0);

        if (selectedRows.length <= 0) {
            errors.intructionValue = validationMessages.proposal_InstructedFeeStageText;
        }
        else if (hasZeroProposalValue) {
            errors.intructionValue = validationMessages.proposal_InstructedValueText;
        }
        else {
            const totalValue = selectedRows.reduce((sum: number, row: { instructedValue: any; }) => {
                const val = Number(row.instructedValue);
                return !isNaN(val) ? sum + val : sum;
            }, 0);

            if (intructionData.intructionValue != totalValue) {
                errors.intructionValue = validationMessages.proposal_InstructionValueCheck;
            }
            else {

                // Map selected rows to FeeStage array
                const proposalFeeStages: ProposalFeeStage[] = selectedRows.map((row) => ({
                    id: row.id,
                    instructedQuantity: row.instructedQuantity,
                    instructedValue: row.instructedValue,
                    completionNotes: row.completionNotes,
                    description: row.description,
                    unitPrice: row.unitPrice,
                    unitPricePer: row.unitPricePer,
                }));

                const instructedFeeStage = {
                    proposalID: Number(proposalId),
                    instructionTypeID: intructionData.intructionType,
                    instructionValue: intructionData.intructionValue,
                    instructionReference: intructionData.instructionReference,
                    createdBy: userId,
                    poAmount:intructionData.poAmount,
                    proposalFrameworkFeeStages: proposalFeeStages
                }

                response = await execute('put', '/Proposal/UpdateProposalFrameworkFeeStage', instructedFeeStage);

                if (response!) {
                    if (response.error) {
                        // If there's an error in the response, display the error message
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        setButtonDisable(true)
                        setToast({ open: true, message: response.data, type: "success" });
                        await delay(1500); // Apply delay for 1.5 seconds if shouldDelay is true
                        afterSubmit();
                    }
                }
            }
        }
        setErrors(errors);
    }

    return (
        <>
            {/* Modal Dialog */}
            <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
                <DialogTitle
                    sx={{
                        position: 'relative',
                        color: '#1e66ab',
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 0,
                        fontWeight: 500,
                        fontSize: '24px'
                    }}>
                    Select Instructed Fee stages

                    <IconButton
                        color="inherit"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8, // Position the button at the right
                            top: 8,   // Position the button at the top
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider></Divider>

                <Box sx={{ width: '100%', textAlign: 'center' }}>
                    {errors.intructionValue && (
                        <Typography className="validationMessage" role="alert" sx={{ mt: 2 }}>
                            {errors.intructionValue}
                        </Typography>
                    )}
                </Box>


                <DialogContent>
                    <Box sx={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={feeStageOption}
                            columns={columns}
                            getRowId={(row) => row.id}
                            // editMode="cell"
                            processRowUpdate={handleProcessRowUpdate}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 5,
                                    },
                                },
                            }}
                            sx={{
                                '& .MuiDataGrid-columnHeaders': {
                                    fontSize: '16px',
                                    color: '#1e66ab',
                                },
                                '& .MuiDataGrid-cell': {
                                    fontSize: '14px',
                                    color: '#1e66ab',
                                    textAlign: 'center',
                                    justifyContent: 'center',
                                },
                                '& .MuiDataGrid-row.editable-row .MuiDataGrid-cellCheckbox svg': {
                                    color: 'green',
                                },
                                '& .MuiDataGrid-row.non-editable-row .MuiDataGrid-cellCheckbox svg': {
                                    color: 'gray',
                                },
                            }}
                            pageSizeOptions={[5]}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selectedIds}
                            onRowSelectionModelChange={(newSelection) => {
                                // Prevent deselection of disabled rows
                                const updatedSelection = Array.from(new Set([...disabledCheckboxes, ...newSelection]));
                                setSelectedIds(updatedSelection);
                            }}
                            isRowSelectable={(params) => !disabledCheckboxes.includes(params.id)}
                            isCellEditable={(params) => !nonEditableRows.includes(params.id)}
                            getCellClassName={(params) => {
                                if (params.field === '__check__' && disabledCheckboxes.includes(params.id)) {
                                    return 'Mui-disabled-checkbox-only';
                                }
                                return '';
                            }}
                            getRowClassName={(params) =>
                                nonEditableRows.includes(params.id) ? 'non-editable-row' : 'editable-row'
                            }
                        />

                        <div
                            style={{
                                display: 'flex',
                                padding: 16,
                                fontWeight: 'bold',
                                color: '#1E66AB',
                            }}
                        >
                            <div style={{ flexBasis: '70%', flexShrink: 0, textAlign: 'right' }}>Total</div>
                            <div style={{ flexBasis: '15%', flexShrink: 0, textAlign: 'right' }}>{totals.quantity}</div>
                            <div style={{ flexBasis: '15%', flexShrink: 0, textAlign: 'right', paddingRight: '4%' }}>{totals.value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</div>

                        </div>
                    </Box>


                    <Grid2 container sx={{ mt: 2 }}>
                        <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                            <CustomTextField
                                label="Instruction Value"
                                name="intructionValue"
                                value={intructionData.intructionValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                                disable={true}
                                sx={{ mb: 1, }}
                                required={true}
                            />
                        </Grid2>

                    </Grid2>



                </DialogContent>
                <DialogActions sx={{ mb: 3, }}>
                    <Box sx={{ width: '100%', textAlign: 'center' }}>
                        <CustomButton
                            capsule
                            sx={{
                                mt: -1,
                                mb: 2,
                                width: '25%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                margin: '0 auto', // Centers horizontally if parent has `display: block`
                                textTransform: 'none'
                            }}
                            disabled={buttonDisable}
                            onClick={handleInstructedFeeStage}
                        >
                            Next
                        </CustomButton>
                    </Box>

                    <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />


                </DialogActions>
            </Dialog>
        </>
    );
}

export default ProposalAddInstructedFeeStage;